import { useEffect, useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';

import { useIntl, defineMessages } from 'react-intl';

import type {
  List,
  Map as ImmutableMap,
  List as ImmutableList,
} from 'immutable';

import type {
  DragStartEvent,
  DragEndEvent,
  UniqueIdentifier,
  Announcements,
  ScreenReaderInstructions,
} from '@dnd-kit/core';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable';

import { changeMediaOrder } from 'mastodon/actions/compose';
import { Button } from 'mastodon/components/button';
import type { MediaAttachment } from 'mastodon/models/media_attachment';
import { useAppSelector, useAppDispatch } from 'mastodon/store';

import { Upload } from './upload';
import { UploadProgress } from './upload_progress';

const messages = defineMessages({
  screenReaderInstructions: {
    id: 'upload_form.drag_and_drop.instructions',
    defaultMessage:
      'To pick up a media attachment, press space or enter. While dragging, use the arrow keys to move the media attachment in any given direction. Press space or enter again to drop the media attachment in its new position, or press escape to cancel.',
  },
  onDragStart: {
    id: 'upload_form.drag_and_drop.on_drag_start',
    defaultMessage: 'Picked up media attachment {item}.',
  },
  onDragOver: {
    id: 'upload_form.drag_and_drop.on_drag_over',
    defaultMessage: 'Media attachment {item} was moved.',
  },
  onDragEnd: {
    id: 'upload_form.drag_and_drop.on_drag_end',
    defaultMessage: 'Media attachment {item} was dropped.',
  },
  onDragCancel: {
    id: 'upload_form.drag_and_drop.on_drag_cancel',
    defaultMessage:
      'Dragging was cancelled. Media attachment {item} was dropped.',
  },
  editMediaTitle: {
    id: 'upload_form.edit_media.title',
    defaultMessage: 'Edit media',
  },
  editMediaDone: {
    id: 'upload_form.edit_media.done',
    defaultMessage: 'Done',
  },
});

interface UploadFormProps {
  onChangeMedia?: (hasMedia: boolean) => void;
}

export const UploadForm: React.FC<UploadFormProps> = ({ onChangeMedia }) => {
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const mediaIds = useAppSelector(
    (state) =>
      (
        (state.compose as ImmutableMap<string, unknown>).get(
          'media_attachments',
        ) as ImmutableList<MediaAttachment>
      ).map((item: MediaAttachment) => item.get('id')) as List<string>,
  );
  const active = useAppSelector(
    (state) => state.compose.get('is_uploading') as boolean,
  );
  const progress = useAppSelector(
    (state) => state.compose.get('progress') as number,
  );
  const isProcessing = useAppSelector(
    (state) => state.compose.get('is_processing') as boolean,
  );
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragStart = useCallback(
    (e: DragStartEvent) => {
      const { active } = e;

      setActiveId(active.id);
    },
    [setActiveId],
  );

  const handleDragEnd = useCallback(
    (e: DragEndEvent) => {
      const { active, over } = e;

      if (over && active.id !== over.id) {
        dispatch(changeMediaOrder(active.id, over.id));
      }

      setActiveId(null);
    },
    [dispatch, setActiveId],
  );

  const handleOpenModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const accessibility: {
    screenReaderInstructions: ScreenReaderInstructions;
    announcements: Announcements;
  } = useMemo(
    () => ({
      screenReaderInstructions: {
        draggable: intl.formatMessage(messages.screenReaderInstructions),
      },

      announcements: {
        onDragStart({ active }) {
          return intl.formatMessage(messages.onDragStart, { item: active.id });
        },

        onDragOver({ active }) {
          return intl.formatMessage(messages.onDragOver, { item: active.id });
        },

        onDragEnd({ active }) {
          return intl.formatMessage(messages.onDragEnd, { item: active.id });
        },

        onDragCancel({ active }) {
          return intl.formatMessage(messages.onDragCancel, { item: active.id });
        },
      },
    }),
    [intl],
  );

  useEffect(() => {
    onChangeMedia?.(mediaIds.size > 0);
  }, [mediaIds.size, onChangeMedia]);

  const displayLimit = 4;
  const layoutClass = Math.min(mediaIds.size, displayLimit);
  const remainingCount = mediaIds.size > displayLimit ? mediaIds.size - displayLimit : 0;
  const displayIds = isModalOpen ? mediaIds : mediaIds.slice(0, displayLimit);

  const dndContextContent = (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      accessibility={accessibility}
    >
      <SortableContext
        items={displayIds.toArray()}
        strategy={rectSortingStrategy}
      >
        {displayIds.map((id, idx) => {
          const isLastVisible = !isModalOpen && idx === displayLimit - 1 && remainingCount > 0;
          const handleOverlayClick = isLastVisible ? handleOpenModal : undefined;

          return (
            <Upload
              key={id}
              id={id}
              dragging={id === activeId}
              tall={!isModalOpen && (displayIds.size < 3 || (displayIds.size === 3 && idx === 0))}
              wide={!isModalOpen && displayIds.size === 1}
              remainingCount={isLastVisible ? remainingCount : 0}
              onOverlayClick={handleOverlayClick}
            />
          );
        })}
      </SortableContext>

      <DragOverlay>
        {activeId ? <Upload id={activeId as string} overlay /> : null}
      </DragOverlay>
    </DndContext>
  );

  const mediaGallery = !isModalOpen ? (
    <div
      className={`compose-form__uploads media-gallery media-gallery--layout-${layoutClass}`}
    >
      {dndContextContent}
    </div>
  ) : null;

  const modalGallery = isModalOpen ? createPortal((
    <div className='upload-modal-overlay' style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.75)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', boxSizing: 'border-box' }}>
      <div className='upload-modal-content' style={{ background: 'var(--background-color, #fff)', borderRadius: '12px', width: '100%', maxWidth: '800px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.3)', boxSizing: 'border-box' }}>
        <div className='upload-modal-header' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', borderBottom: '1px solid var(--background-border-color, #eee)', boxSizing: 'border-box' }}>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold', color: 'var(--primary-text-color)' }}>{intl.formatMessage(messages.editMediaTitle)}</h2>
          <Button onClick={handleCloseModal} text={intl.formatMessage(messages.editMediaDone)} />
        </div>
        <div className='upload-modal-body' style={{ padding: '16px', overflowY: 'auto', flex: 1, background: 'var(--input-background-color, #f0f2f5)', boxSizing: 'border-box' }}>
          <style>
            {`
              .upload-modal-body .compose-form__upload__thumbnail {
                background-color: var(--background-color, #fff);
                border: 1px solid var(--background-border-color, #ddd);
                border-radius: 8px;
              }
              .upload-modal-body .compose-form__upload {
                border-radius: 8px;
                overflow: hidden;
                aspect-ratio: 1;
              }
            `}
          </style>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '16px', width: '100%', boxSizing: 'border-box' }}>
            {dndContextContent}
          </div>
        </div>
      </div>
    </div>
  ), document.body) : null;

  const content = (
    <>
      {mediaGallery}
      {modalGallery}
    </>
  );

  return (
    <>
      <UploadProgress
        active={active}
        progress={progress}
        isProcessing={isProcessing}
      />

      {mediaIds.size > 0 &&
        content}
    </>
  );
};
